import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Text } from "#components/atoms/Typographies/Text";
import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import { useMarkOptimizationNotificationsRead } from "#modules/optimizations/view/useMarkOptimizationNotificationsRead";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";

const THUMBNAIL_SIZE = 48;

type DeletedNotification = OptimizationDto["optimizationNotifications"][number];

type Props = {
  notifications: DeletedNotification[];
  optimizationId: string;
  quoteRequestId: string;
};

export const DeletedLinesAlert = ({ notifications, optimizationId, quoteRequestId }: Props) => {
  const { t } = useTranslation();
  const { markOptimizationNotificationsRead } = useMarkOptimizationNotificationsRead({
    quoteRequestId,
  });
  const isSingleNotification = notifications.length === 1;
  const singleNotification = notifications[0];

  if (!singleNotification) {
    return null;
  }

  return (
    <Flex align="center" gap={spacing[2]} px={spacing[6]} py={spacing[3]}>
      {isSingleNotification ? (
        <ThumbnailOverlay
          part={singleNotification.part}
          size={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
        />
      ) : (
        <Box
          aria-hidden
          bg={colors.bg.elevation.quaternary}
          w={THUMBNAIL_SIZE}
          h={THUMBNAIL_SIZE}
          style={{
            borderRadius: radius.xs,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon name="trash" size={16} color={colors.icon.primaryDefault} />
        </Box>
      )}
      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
        <Text size="small" type="medium">
          {isSingleNotification
            ? t("optimizations.deleted.singleTitle", { name: singleNotification.part.name })
            : t("optimizations.deleted.severalTitle", { count: notifications.length })}
        </Text>
        <Text size="tiny" color={colors.text.secondaryDefault}>
          {t("optimizations.deleted.description")}
        </Text>
      </Stack>
      <Box style={{ flexShrink: 0 }}>
        <Button
          variant="tertiary"
          size="md"
          onClick={() =>
            markOptimizationNotificationsRead({
              optimizationId,
              notificationIds: notifications.map((notification) => notification.id),
            })
          }
        >
          {t("optimizations.deleted.acknowledge")}
        </Button>
      </Box>
    </Flex>
  );
};
